import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/button/button';
import InputField from 'src/components/admin/form/inputField';
import InputLocationField from 'src/components/admin/form/inputLocationField';
import DraggableMapView from 'src/components/MapView/DraggableMapView';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, ModalHeader, ModalProps } from 'flowbite-react';
import { Controller, useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { TouristCenterOverrideType } from 'src/types/touristCenterOverride';
import { HiOutlineInformationCircle, HiOutlineSearch } from 'react-icons/hi';
import { useCreateTouristCenterOverride, useUpdateTouristCenterOverride } from 'src/hooks/useTouristCenterOverrides';

export type AddEditTouristCenterModalProps = ModalProps & {
   selectedOverride?: TouristCenterOverrideType | null;
   onClose: () => void;
   openModal: boolean;
};

const AddEditTouristCenterModal: React.FC<AddEditTouristCenterModalProps> = ({ openModal, selectedOverride, onClose }) => {
   const [t] = useTranslation();
   const headerTheme = {
      base: 'flex justify-center text-center !pt-7.5 !px-7.5 border-b-0 ',
      title: 'font-bold text-sx',
      close: { base: 'absolute right-2 top-2 rounded-md cursor-pointer ', icon: 'text-primaryInteract' },
   };

   const defaultValues = useMemo(
      () => ({
         mapboxId: selectedOverride?.mapboxId || '',
         searchLocation: selectedOverride?.searchLocation || '',
         overrideName: selectedOverride?.overrideName || '',
         overrideLatitude: parseFloat(String(selectedOverride?.overrideLatitude || 40.758)).toFixed(4),
         overrideLongitude: parseFloat(String(selectedOverride?.overrideLongitude || -73.9855)).toFixed(4),
         notes: selectedOverride?.notes || '',
      }),
      [selectedOverride],
   );

   const {
      control,
      formState: { errors },
      reset,
      clearErrors,
      handleSubmit,
      setValue,
      watch,
   } = useForm({
      mode: 'onChange',
      defaultValues,
      resolver: yupResolver(
         object().shape({
            mapboxId: string().required(t('validations.required')),
            overrideLatitude: string().required(t('validations.required')),
            overrideLongitude: string().required(t('validations.required')),
            searchLocation: string().required(t('validations.required')),
            overrideName: string(),
            notes: string(),
         }),
      ),
   });

   const watchLatitude = watch('overrideLatitude');
   const watchLongitude = watch('overrideLongitude');

   useEffect(() => {
      if (openModal) {
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = '';
      }
      return () => {
         document.body.style.overflow = '';
      };
   }, [openModal]);

   useEffect(() => {
      reset({
         mapboxId: selectedOverride?.mapboxId || '',
         searchLocation: selectedOverride?.searchLocation || '',
         overrideName: selectedOverride?.overrideName || '',
         overrideLatitude: parseFloat(String(selectedOverride?.overrideLatitude || 40.758)).toFixed(4),
         overrideLongitude: parseFloat(String(selectedOverride?.overrideLongitude || -73.9855)).toFixed(4),
         notes: selectedOverride?.notes || '',
      });
   }, [selectedOverride, reset]);

   const { mutateAsync: addMutation, isPending: isCreating } = useCreateTouristCenterOverride({
      onSuccessCallBack: () => {
         clearErrors();
         reset();
         onClose?.();
      },
   });

   const { mutateAsync: updateMutation, isPending: isUpdating } = useUpdateTouristCenterOverride({
      onSuccessCallBack: () => {
         clearErrors();
         reset();
         onClose?.();
      },
   });

   interface FormValues {
      mapboxId: string;
      searchLocation: string;
      overrideName?: string;
      overrideLatitude: string;
      overrideLongitude: string;
      notes?: string;
   }

   const onSubmit = async (value: FormValues) => {
      const payload = {
         mapboxId: value.mapboxId,
         overrideLatitude: parseFloat(value.overrideLatitude),
         overrideLongitude: parseFloat(value.overrideLongitude),
         searchLocation: value.searchLocation || undefined,
         overrideName: value.overrideName || undefined,
         notes: value.notes || undefined,
      };

      if (selectedOverride) {
         await updateMutation({
            id: selectedOverride.id,
            payload,
         });
      } else {
         await addMutation(payload);
      }
   };

   const handleLocationSelect = (locationData: any) => {
      if (locationData?.mapboxId) {
         setValue('mapboxId', locationData.mapboxId);
         setValue('searchLocation', locationData.place_name);
         if (locationData.center) {
            setValue('overrideLatitude', parseFloat(locationData.center[1].toFixed(4)).toString());
            setValue('overrideLongitude', parseFloat(locationData.center[0].toFixed(4)).toString());
         }
      }
   };

   const handleCoordinateChange = (coords: { lat: number; lng: number }) => {
      setValue('overrideLatitude', parseFloat(coords.lat.toFixed(4)).toString());
      setValue('overrideLongitude', parseFloat(coords.lng.toFixed(4)).toString());
   };

   return (
      <Modal
         show={openModal}
         onClose={() => {
            reset();
            onClose();
         }}
         size="6xl"
         theme={{
            content: {
               inner: 'relative flex flex-col max-h-[90dvh] overflow-hidden rounded-lg bg-white shadow',
            },
         }}
      >
         <ModalHeader theme={headerTheme}>{selectedOverride ? t('Edit Location Override') : t('Add Location Overrides')}</ModalHeader>
         <form
            onSubmit={e => {
               e.stopPropagation();
               handleSubmit(onSubmit)(e);
            }}
            className="flex flex-col flex-1 min-h-0"
         >
            <div className="px-7.5 overflow-y-auto flex-1 pb-6">
               {/* Pre-seed Instructions */}
               <div className="mb-6 p-4 bg-blue-50 border-l-4 border-l-blue-600 border-t border-t-blue-200 border-r border-r-blue-200 border-b border-b-blue-200 rounded">
                  <div className="flex gap-2 items-center">
                     <HiOutlineInformationCircle className="text-blue-600 flex-shrink-0" size={20} />
                     <p className="font-bold text-black text-lg">Pre-seed Instructions</p>
                  </div>
                  <ul className="space-y-1 ml-2 list-inside text-sm text-black" style={{ listStyleType: 'square' }}>
                     <li className="font-semibold">Specify distinct areas for existing city search terms.</li>
                     <li className="font-semibold">Use the search box below to pick a city. Then, refine the exact location on the map.</li>
                     <li className="font-normal text-blue-600 mt-1 ml-0">Examples:</li>
                     <li className="list-none ml-4">- Search: "Las Vegas" → Override name: "The Strip"</li>
                     <li className="list-none ml-4">- Search: "Orlando" → Override name: "Near Disney"</li>
                     <li className="list-none ml-4">- Search: "New York City" → Override name: "Times Square Area" (default)</li>
                  </ul>
               </div>

               {/* 2-Column Layout: 40% form, 60% map */}
               <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-3.75 border border-border rounded-6">
                  {/* Left Column: Search & Details (40%) */}
                  <div className="lg:col-span-2">
                     <h3 className="text-lg font-bold text-black mb-4">Search & Details</h3>
                     <div className="flex flex-col gap-4">
                        <Controller
                           name="searchLocation"
                           control={control}
                           render={({ field: { onChange, value } }) => (
                              <div>
                                 <p className="m-0 font-semibold text-sm mb-2.5">Search Location *</p>
                                 <InputLocationField
                                    error={errors?.searchLocation?.message}
                                    placeholder="New York City!"
                                    value={value}
                                    startIcon={<HiOutlineSearch size={18} className="text-primary" />}
                                    onChange={(place_name?: string, center?: any, context?: any, mapboxId?: string) => {
                                       onChange(place_name);
                                       if (place_name && center && mapboxId) {
                                          handleLocationSelect({
                                             mapboxId,
                                             place_name,
                                             center,
                                             context,
                                          });
                                       }
                                    }}
                                 />
                              </div>
                           )}
                        />
                        <Controller
                           name="overrideName"
                           control={control}
                           render={({ field: { onChange, value } }) => (
                              <InputField
                                 error={errors?.overrideName?.message}
                                 title={
                                    <>
                                       Override Name <span className="font-normal">(Optional)</span>
                                    </>
                                 }
                                 placeholder="Times Square Area"
                                 value={value}
                                 onChange={onChange}
                              />
                           )}
                        />
                        <Controller
                           name="notes"
                           control={control}
                           render={({ field: { onChange, value } }) => (
                              <InputField
                                 theme={{ border: 'h-fit', input: 'py-2' }}
                                 error={errors?.notes?.message}
                                 title={
                                    <>
                                       Notes <span className="font-normal">(Optional)</span>
                                    </>
                                 }
                                 placeholder="Description here ..."
                                 value={value}
                                 onChange={onChange}
                                 multiline={3}
                              />
                           )}
                        />
                        <div className="grid grid-cols-2 gap-3">
                           <Controller
                              name="overrideLatitude"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                 <InputField
                                    error={errors?.overrideLatitude?.message}
                                    title="Override Latitude *"
                                    placeholder="e.g., 36.1147"
                                    type="text"
                                    value={value}
                                    onChange={onChange}
                                    disabled={true}
                                 />
                              )}
                           />
                           <Controller
                              name="overrideLongitude"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                 <InputField
                                    error={errors?.overrideLongitude?.message}
                                    title="Override Longitude *"
                                    placeholder="e.g., -115.1728"
                                    type="text"
                                    value={value}
                                    onChange={onChange}
                                    disabled={true}
                                 />
                              )}
                           />
                        </div>
                     </div>
                  </div>

                  {/* Right Column: Map View (60%) */}
                  <div className="flex-1 lg:col-span-3 flex flex-col min-h-[412px]">
                     <h3 className="text-lg font-bold text-black mb-4 text-center">Map Preview</h3>
                     {watchLatitude && watchLongitude && (
                        <DraggableMapView
                           id="tourist-center-map"
                           latitude={parseFloat(watchLatitude)}
                           longitude={parseFloat(watchLongitude)}
                           onCoordinateChange={handleCoordinateChange}
                        />
                     )}
                  </div>
               </div>
            </div>
            <div className="flex px-7.5 py-5 gap-x-4 justify-center shrink-0 border-t border-gray-100">
               <Button
                  className="h-9 min-w-[102px] border text-sm border-primary hover:bg-secondary hover:text-white text-primary"
                  outline
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                     e.preventDefault();
                     e.stopPropagation();
                     clearErrors();
                     reset();
                     onClose();
                  }}
               >
                  {t('common.cancel')}
               </Button>
               <Button className="h-9 min-w-[143px] text-sm" type="submit" disabled={isCreating || isUpdating}>
                  {isCreating || isUpdating ? t('common.saving') : selectedOverride ? 'Save Override' : 'Save Override'}
               </Button>
            </div>
         </form>
      </Modal>
   );
};

export default AddEditTouristCenterModal;
